import React, { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "components/Badge";

import { parseDate, getArticleCardHref, sizeEqualOrLessThan } from "utils/helpers";
import { colors } from "utils/styles";
import { ContenfulQuickLink } from "./DebugTools";
import Text from "components/Reusables/Text";
import Title from "components/Reusables/Title";
import useJournalAuth from "hooks/useJournalAuth";
import ArticleBody from "./Reusables/ArticleBody";
import SocialShareIcons from "components/SocialShareIcons";
import useScreenSize from "hooks/useScreenSize";
import LibraryFilterBar from "components/Reusables/FilterBar/LibraryFilterBar";
import FormEmbed from "@components/Reusables/FormEmbed";

const MarkdownContent = dynamic(() => import("./Reusables/MarkdownContent"));
// const GeneralRichText = dynamic(() => import("./Reusables/GeneralRichText"));
const Image = dynamic(() => import("./Reusables/Image"));
const DynamicLink = dynamic(() => import("./Reusables/DynamicLink"));

export default function Post({
	post,
	// childTags,
	relatedArticles,
}) {
	if (!post) return <>Post not found</>;

	const { width, size } = useScreenSize();
	const [mdOrSmaller, setMdOrSmaller] = useState(true);

	useEffect(() => {
		const mdOrSmaller = sizeEqualOrLessThan("md");
		setMdOrSmaller(mdOrSmaller);
	}, [width, size]);

	const { ShowArticleAccessDenied, articleAccessClass } = useJournalAuth(post.fields);

	const { fields } = post;

	const bannerImage = fields.bannerImage?.fields;

	const pubDate = parseDate(fields.publicationDate);

	let authorNames = "";
	if (fields?.author?.length) {
		fields.author = fields?.author.filter((author) => author.fields);
		fields?.author.forEach((author, index) => {
			const isLastAuthor = index === fields.author.length - 1;
			authorNames += `${author.fields.name}${!isLastAuthor ? " | " : ""}`;
		});
	}

	return (
		<Container className="  mx-auto relative">
			{process.env.APP_ENV !== "production" && <ContenfulQuickLink entryId={post.sys.id} IR={true} />}
			{/* <DynamicLink color={colors["green"]} classes="mb-1 d-inline-block" href="/about/press-room">
				<span className="mb-0 mt-0">&laquo; Return to Press Room</span>
			</DynamicLink> */}
			<div className=" row relative">
				<Row className="g-0">
					<Col>
						<LibraryFilterBar />
					</Col>
				</Row>
				<article className="blog-article-container mt-4 ">
					{fields.primaryTag && (
						<Text
							weight="bold"
							size="16"
							color={colors.green}
							text={fields.primaryTag?.parentTag?.tagTitle}
							className="m-0 d-flex justify-content-center"
							marginBottom="0"
						/>
					)}
					{fields.isSponsored === true && (
						<Text
							className="m-0 d-flex justify-content-center"
							marginBottom="3"
							text={"Sponsored Content"}
							color={colors.dark}
							weight={"bold"}
							size={14}
						/>
					)}
					<header>
						<Title
							className="text-center mt-1 mb-2"
							lineHeight={mdOrSmaller ? "40" : "60"}
							fontSize={mdOrSmaller ? "36px" : "56px"}
							text={fields.title}
							justify="center"
						/>

						{authorNames && <p className="by-author">{authorNames}</p>}
						<small className=" text-center">
							{/* <div className="date-line" /> */}
							<p className="publication-date">{`${pubDate.fullMonthName} ${pubDate.dayOfMonth}, ${pubDate.year}`}</p>
						</small>

						{fields.peerReview && (
							<Col xs={"auto"}>
								<Badge title={"Peer-Reviewed"} label={"Peer-Reviewed"} color={"red"} fontSize="15" />
							</Col>
						)}
						<br />
						<div className="row g-1 d-flex align-items-center relative">
							<div className="absolute d-none d-md-flex  ">
								<SocialShareIcons article={post} />
							</div>
							<Col xs="auto" className=" d-inline-flex d-md-none  ">
								<SocialShareIcons article={post} />
							</Col>
							{bannerImage !== undefined ? (
								<Col className=" justify-content-center d-flex">
									<Image
										fields={{ media: { fields: bannerImage } }}
										extraClass=" d-inline-flex w-100 boxShadow"
										// height={680}
										maxWidth={930}
										maxHeight={500}
									/>
								</Col>
							) : (
								<Col xs="" className="justify-content-center d-flex">
									<img
										className="mh-500px mw-930px"
										src="https://images.ctfassets.net/mdag2z2o3c18/73533DQHZgyUDdrQ0j4iwo/d2ac1fd3ae5f15103e3ce6f107e3d7cb/aapl-logo.png"
										alt="America Association of Physician Leaders logo"
									/>
								</Col>
							)}
						</div>
					</header>

					<section className="blog-article">
						<div className="article-content">
							{fields.summary && !fields.abstract && (
								<div className="px-3 px-lg-0">
									<p className="paragraph summary-label">Summary: </p>
									<MarkdownContent className="summary d-inline" markdown={fields.summary} />
									<br />
									<br />
									<br />
									<hr />
								</div>
							)}
							{fields.abstract && (
								<div className="px-3 px-lg-0">
									<p className="paragraph summary-label">Abstract: </p>

									<MarkdownContent className="summary d-inline" markdown={fields.abstract} />
									<br />
									<br />
									<hr />
								</div>
							)}

							<Row className="g-0">
								<Col className="px-3 px-lg-0" md={8}>
									<ArticleBody fields={fields} articleAccessClass={articleAccessClass} />
									{fields.formId !== undefined && (
										<FormEmbed
											data={{
												fields: {
													horizontalAlign: "center",
													formClasses: "d-flex flex-column p-4  radius-5px",
													containerClasses: "d-flex  w-100 p-1 p-sm-2 p-md-4",
													portalId: "2735001",
													formId: fields.formId,
												},
											}}
										/>
									)}
								</Col>
								<Col md={{ offset: 1, span: 3 }} className="d-none d-md-block">
									<Text text="Topics" weight="bold" size="18" marginBottom="0" />
									{fields.primaryTag && (
										<Text
											size="18"
											color={colors.green}
											className="m-0 d-flex"
											marginBottom="0"
											text={fields.primaryTag.title}
										/>
									)}
									{fields.secondaryTag && (
										<Text
											size="18"
											color={colors.green}
											className="m-0 d-flex"
											marginBottom="0"
											text={fields.secondaryTag.title}
										/>
									)}
									{fields.tertiaryTag && (
										<Text
											size="18"
											color={colors.green}
											className="m-0 d-flex"
											marginBottom="0"
											text={fields.tertiaryTag.title}
										/>
									)}

									<br />
									<Text text="Related" weight="bold" size="18" marginBottom="0" />
									{relatedArticles?.length > 0 &&
										relatedArticles?.slice(0, 3).map((article) => {
											return (
												<DynamicLink
													key={article.canonicalUrl}
													classes="d-block mb-4"
													href={getArticleCardHref(article)}
													text={article.title}
													color={colors.dark}
													textDecoration="underline"
												/>
											);
										})}
								</Col>
								{!ShowArticleAccessDenied && (
									<Col className="px-3 px-lg-0" md={{ offset: 1, span: 9 }}>
										<Title text="Recommended Reading" element="h3" />
										<Row className="">
											{relatedArticles?.length > 0 &&
												relatedArticles?.map((article) => {
													return (
														<Col key={article.canonicalUrl} sm={6} className="mb-4">
															<Text
																weight="bold"
																size="16"
																color={colors.green}
																text={article.primaryTag}
																className="m-0 d-flex"
																marginBottom="0"
															/>
															<DynamicLink href={getArticleCardHref(article)}>
																<Text
																	size="16"
																	text={article.title}
																	className="m-0 d-flex"
																	marginBottom="0"
																/>
															</DynamicLink>
														</Col>
													);
												})}
										</Row>
									</Col>
								)}
							</Row>
						</div>

						<hr />
					</section>
				</article>

				{ShowArticleAccessDenied}
			</div>
			<style global jsx>{`
				.date-line {
					width: 20px;
					border-top: 3px solid ${colors.darkBlue};
					margin-top: 15px;
				}
				.blog-article-container .publication-date {
					font-size: 12px !important;
					font-weight: 700;
					margin-top: 0 !important;
				}
				.by-author {
					font-size: 16px;
					color: #616161 !important;
					font-family: "Open Sans", serif;
					margin: 0;
					margin-bottom: 1em;
					text-align: center;
				}

				.blog-article h1 {
					font-size: 56px;
					line-height: 60px;
					margin-top: 10px !important;
					margin-bottom: 18px !important;
					color: ${colors.dark} !important;
					font-family: "Athelas";
					font-weight: bold;
				}
				.blog-article-container h2 {
					margin-top: 36px !important;
					margin-bottom: 15px !important;
					font-size: 36px !important;
					font-weight: 600;
					color: #444444 !important;
					line-height: 36px;
				}

				.summary-label {
					font-size: 17px !important;
					font-weight: 700;
					display: inline;
				}
				.blog-article-container .summary {
					margin-top: 0px !important;
					margin-bottom: 20px !important;
					font-size: 17px;
					font-family: Open Sans;
				}
				.tags {
					margin-top: 20px;
				}

				.blog-article-container header {
					margin-bottom: 3em;
					/* padding-bottom: 2rem; */

					text-align: start;
				}
				article.blog-article-container {
					max-width: 930px;
					margin-left: auto;
					margin-right: auto;
				}
				.blog-article {
					font-family: "Open Sans", serif !important;
				}

				.blog-article-container a:hover {
					color: #142541;
					text-decoration: underline;
				}

				.blog-article-container a {
					color: ${colors["green"]};
				}
			`}</style>
		</Container>
	);
}
